import { apiClient } from '@/shared/api';

import type {
  AttributeValueSetRequest,
  BrowseProductsParams,
  CatalogVersionDetail,
  CatalogVersionSummaryPage,
  ImportResult,
  ProductBarcode,
  ProductBarcodeCreateRequest,
  ProductDetail,
  ProductFamilyAssignRequest,
  ProductListRequest,
  ProductListResponse,
  ProductPublication,
  ProductSearchRequest,
  ProductSearchResponse,
  ProductStateUpdateRequest,
  ProductSuggestRequest,
  ProductSummaryPage,
  ProductTranslation,
  ProductUpsertRequest,
  ProductVariantCreateRequest,
  SuggestResponse,
} from '../model/types';

const BASE = '/api/v1/catalog/products';

export const listProducts = async (body: ProductListRequest = {}): Promise<ProductListResponse> =>
  (await apiClient.post<ProductListResponse>(BASE, body)).data;

/**
 * Browse the FULL master catalog (drafts included) — epic-catalog-002, story-024.
 * POST so the filter (incl. exact UUID `ids`) rides in the body and is searched in OpenSearch.
 */
export const browseProducts = async (
  filter: BrowseProductsParams = {},
): Promise<ProductSummaryPage> =>
  (await apiClient.post<ProductSummaryPage>(`${BASE}/browse`, filter)).data;

export const deleteProduct = async (productId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${productId}`);
};

export const searchProducts = async (body: ProductSearchRequest): Promise<ProductSearchResponse> =>
  (await apiClient.post<ProductSearchResponse>(`${BASE}/search`, body)).data;

export const suggestProducts = async (body: ProductSuggestRequest): Promise<SuggestResponse> =>
  (await apiClient.post<SuggestResponse>(`${BASE}/suggest`, body)).data;

export const getProduct = async (productId: string): Promise<ProductDetail> =>
  (await apiClient.get<ProductDetail>(`${BASE}/${productId}`)).data;

/**
 * Every stored translation of a product (epic-catalog-004, adr-tenant-005, plan-tenant-004
 * Wave 2) — unlike `getProduct`, not filtered/fallen-back to one locale. Feeds the merchant-portal
 * card-language switcher (`ProductEditPage`/`useCardLanguageDraft`).
 */
export const getProductTranslations = async (productId: string): Promise<ProductTranslation[]> =>
  (await apiClient.get<ProductTranslation[]>(`${BASE}/${productId}/translations`)).data;

export const upsertProduct = async (
  productId: string,
  body: ProductUpsertRequest,
): Promise<ImportResult> => (await apiClient.put<ImportResult>(`${BASE}/${productId}`, body)).data;

// updateProductStock / bulkUpdateProductsStock removed (plan-catalog-007): the master owns no
// stock — quick/bulk stock edits are inventory MANUAL_CORRECTION adjustments via
// api/productStockApi.ts (quickSetStock / bulkQuickSetStock).

export const updateProductState = async (
  productId: string,
  body: ProductStateUpdateRequest,
): Promise<ProductDetail> =>
  (await apiClient.put<ProductDetail>(`${BASE}/${productId}/state`, body)).data;

// ── Publication (publish / unpublish / archive / state) ──────────────────────
export const publishProduct = async (
  productId: string,
  channelCode?: string,
): Promise<ProductPublication> =>
  (
    await apiClient.post<ProductPublication>(
      `${BASE}/${productId}/publish`,
      undefined,
      channelCode ? { params: { channelCode } } : undefined,
    )
  ).data;

export const unpublishProduct = async (
  productId: string,
  channelCode?: string,
): Promise<ProductPublication> =>
  (
    await apiClient.post<ProductPublication>(
      `${BASE}/${productId}/unpublish`,
      undefined,
      channelCode ? { params: { channelCode } } : undefined,
    )
  ).data;

export const archiveProduct = async (productId: string): Promise<ProductPublication> =>
  (await apiClient.post<ProductPublication>(`${BASE}/${productId}/archive`)).data;

export const restoreProduct = async (productId: string): Promise<ProductPublication> =>
  (await apiClient.post<ProductPublication>(`${BASE}/${productId}/restore`)).data;

export const getProductPublication = async (productId: string): Promise<ProductPublication> =>
  (await apiClient.get<ProductPublication>(`${BASE}/${productId}/publication`)).data;

// ── Product PIM v2: family assignment, typed attribute values, variants, version history
//    (epic-catalog-004/005, user-story-merchant-portal-030/031/032) ─────────────────────────

/** Assign a product-family (attribute template) to a product. */
export const assignProductFamily = async (
  productId: string,
  body: ProductFamilyAssignRequest,
): Promise<ProductDetail> =>
  (await apiClient.put<ProductDetail>(`${BASE}/${productId}/family`, body)).data;

/** Unassign the product-family from a product. */
export const unassignProductFamily = async (productId: string): Promise<ProductDetail> =>
  (await apiClient.delete<ProductDetail>(`${BASE}/${productId}/family`)).data;

/**
 * Set a typed attribute value on a product — validates against the attribute's type and rules;
 * rejected with `422 ATTRIBUTE_VALIDATION_FAILED` (details: attribute/rule/expected) on failure.
 */
export const setProductAttributeValue = async (
  productId: string,
  attributeCode: string,
  body: AttributeValueSetRequest,
): Promise<ProductDetail> =>
  (
    await apiClient.put<ProductDetail>(
      `${BASE}/${productId}/attributes/${encodeURIComponent(attributeCode)}`,
      body,
    )
  ).data;

/** Clear a product's attribute value — idempotent (clearing an absent value still returns the current card). */
export const deleteProductAttributeValue = async (
  productId: string,
  attributeCode: string,
): Promise<ProductDetail> =>
  (
    await apiClient.delete<ProductDetail>(
      `${BASE}/${productId}/attributes/${encodeURIComponent(attributeCode)}`,
    )
  ).data;

/**
 * Create ONE product variant along the parent's variant axis — there is no batch endpoint;
 * generating several combinations means one call per combination (see `useCreateProductVariants`'s
 * fan-out). `409` on a duplicate axis-value combination or taken code; `422` if the parent is itself
 * a variant or the request is missing an axis value.
 */
export const createProductVariant = async (
  productId: string,
  body: ProductVariantCreateRequest,
): Promise<ProductDetail> =>
  (await apiClient.post<ProductDetail>(`${BASE}/${productId}/variants`, body)).data;

/** List a product's variants, in a stable order. */
export const listProductVariants = async (
  productId: string,
  locale?: string,
): Promise<ProductDetail[]> =>
  (
    await apiClient.get<ProductDetail[]>(`${BASE}/${productId}/variants`, {
      params: locale ? { locale } : undefined,
    })
  ).data;

/** Product version history, paged, newest first. */
export const getProductVersions = async (
  productId: string,
  params: { page?: number; size?: number } = {},
): Promise<CatalogVersionSummaryPage> =>
  (await apiClient.get<CatalogVersionSummaryPage>(`${BASE}/${productId}/versions`, { params })).data;

/** Full diff of one product version. */
export const getProductVersion = async (
  productId: string,
  number: number,
): Promise<CatalogVersionDetail> =>
  (await apiClient.get<CatalogVersionDetail>(`${BASE}/${productId}/versions/${number}`)).data;

/** Restore a product to a prior version (creates a new version — never rewrites history). Requires `catalog.manage`. */
export const restoreProductVersion = async (
  productId: string,
  number: number,
): Promise<ProductDetail> =>
  (await apiClient.post<ProductDetail>(`${BASE}/${productId}/versions/${number}/restore`)).data;

// ── Product barcodes (epic-002, user-story-merchant-portal-039) ─────────────────────────────

/** A product's full barcode history (ACTIVE + RETIRED, primary first). */
export const getProductBarcodes = async (productId: string): Promise<ProductBarcode[]> =>
  (await apiClient.get<ProductBarcode[]>(`${BASE}/${productId}/barcodes`)).data;

/** Attach a manual value, or allocate a fresh EAN-13 (`{generate: true}`). 400/409 carry a server message. */
export const createProductBarcode = async (
  productId: string,
  body: ProductBarcodeCreateRequest,
): Promise<ProductBarcode> =>
  (await apiClient.post<ProductBarcode>(`${BASE}/${productId}/barcodes`, body)).data;

/** Designate a barcode as the product's primary (clears any previous primary). */
export const setPrimaryProductBarcode = async (productId: string, barcodeId: string): Promise<void> => {
  await apiClient.put(`${BASE}/${productId}/barcodes/${barcodeId}/primary`);
};

/** Soft-retire a barcode (`status -> RETIRED`, frees its value for re-issue). */
export const deleteProductBarcode = async (productId: string, barcodeId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${productId}/barcodes/${barcodeId}`);
};

// ── Variant matrix & inheritance overrides (epic-catalog-007, P8) ───────────────────────────────

export const generateVariantMatrix = async (
  parentId: string,
  body: import('../model/types').VariantMatrixGenerateRequest,
): Promise<import('../model/types').VariantMatrixGenerateResult> =>
  (await apiClient.post(`/catalog/products/${parentId}/variants/generate`, body)).data;

export const resetProductImagesOverride = async (productId: string): Promise<ProductDetail> =>
  (await apiClient.delete(`/catalog/products/${productId}/images/override`)).data;

export const resetProductCategoryOverride = async (productId: string): Promise<ProductDetail> =>
  (await apiClient.delete(`/catalog/products/${productId}/category/override`)).data;

export const listProductAxisMedia = async (
  productId: string,
): Promise<import('../model/types').AxisValueMediaItem[]> =>
  (await apiClient.get(`/catalog/products/${productId}/axis-media`)).data;

export const replaceProductAxisMedia = async (
  productId: string,
  attributeCode: string,
  optionCode: string,
  imageUrls: string[],
): Promise<import('../model/types').AxisValueMediaItem[]> =>
  (
    await apiClient.put(`/catalog/products/${productId}/axis-media/${attributeCode}/${optionCode}`, {
      imageUrls,
    })
  ).data;

export const deleteProductAxisMedia = async (
  productId: string,
  attributeCode: string,
  optionCode: string,
): Promise<void> => {
  await apiClient.delete(`/catalog/products/${productId}/axis-media/${attributeCode}/${optionCode}`);
};
